import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const SUPERADMIN_EMAIL = "oliwer@elmarknad.se";
export const PREVIEW_PARTNER_KEY = "em.previewPartnerId";

export interface PartnerRow {
  id: string;
  name: string;
  email: string;
  lead_types: string[];
  is_active: boolean;
  is_exclusive: boolean;
  is_internal: boolean;
  notes: string | null;
  advertiser_offer_id: string | null;
  contacts: unknown;
  org_number: string | null;
  phone: string | null;
  website: string | null;
  billing_email: string | null;
  billing_reference: string | null;
  billing_address: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  lead_type_emails: unknown;
}

export interface PartnerAccessState {
  loading: boolean;
  user: User | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** Partnerprofilen som visas (egen, eller vald partner när superadmin förhandsgranskar). */
  partner: PartnerRow | null;
  /** true om det inloggade kontot självt matchar en aktiv leadpartner. */
  isPartner: boolean;
  /** Sätt vald partner (endast admin). */
  previewPartnerId: string | null;
  setPreviewPartnerId: (id: string | null) => void;
  refresh: () => Promise<void>;
}

/** True om partnern aktivt valt att ta emot (tjänste)leads. */
export function partnerReceivesLeads(partner: { lead_types?: string[] | null } | null): boolean {
  const types = partner?.lead_types ?? [];
  return types.length > 0;
}

const readPreview = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(PREVIEW_PARTNER_KEY);
};

export function usePartnerAccess(): PartnerAccessState {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [partner, setPartner] = useState<PartnerRow | null>(null);
  const [isPartner, setIsPartner] = useState(false);
  const [previewPartnerId, setPreviewId] = useState<string | null>(null);

  useEffect(() => {
    setPreviewId(readPreview());
  }, []);

  const setPreviewPartnerId = useCallback((id: string | null) => {
    if (typeof window !== "undefined") {
      if (id) window.localStorage.setItem(PREVIEW_PARTNER_KEY, id);
      else window.localStorage.removeItem(PREVIEW_PARTNER_KEY);
    }
    setPreviewId(id);
  }, []);

  const evaluate = useCallback(async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    setUser(currentUser);

    if (!currentUser) {
      setIsAdmin(false);
      setPartner(null);
      setIsPartner(false);
      setLoading(false);
      return;
    }

    const { data: adminResult } = await supabase.rpc("is_admin");
    const admin = Boolean(adminResult);
    setIsAdmin(admin);

    const { data: ownId } = await supabase.rpc("current_partner_id");
    setIsPartner(Boolean(ownId));

    const targetId = admin && previewPartnerId ? previewPartnerId : (ownId as string | null);
    if (targetId) {
      const { data } = await supabase
        .from("lead_partners")
        .select("*")
        .eq("id", targetId)
        .maybeSingle();
      setPartner((data as PartnerRow) ?? null);
    } else {
      setPartner(null);
    }
    setLoading(false);
  }, [previewPartnerId]);

  useEffect(() => {
    void evaluate();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void evaluate();
    });
    return () => subscription.unsubscribe();
  }, [evaluate]);

  const isSuperAdmin = (user?.email ?? "").toLowerCase() === SUPERADMIN_EMAIL;

  return {
    loading,
    user,
    isAdmin,
    isSuperAdmin,
    partner,
    isPartner,
    previewPartnerId,
    setPreviewPartnerId,
    refresh: evaluate,
  };
}
